// Lazy loaders for MathJax v4 newcm dynamic font chunks.
//
// MathJax v4 splits the New Computer Modern glyph data into on-demand chunks
// (`monospace`, `script`, `sans-serif`, `calligraphic`, ...) fetched the
// first time a command needs them. MathJax's stock async loader uses
// `import(file)` with a RUNTIME string, which esbuild (this editor's bundler)
// cannot statically resolve, so the fetch fails inside the Angular bundle and
// commands like \mathtt, \mathsf, \mathscr, \mathcal raise
// "dynamic file '<chunk>' failed to load" in the editor. See work item #1660.
//
// Each entry below is a statically-analysable `import()` so esbuild emits a
// real chunk fetched on first use. This mirrors the ee editor's loader
// (apps/projects/components/.../mathjax/font-loaders.ts); kept here because the
// sfo editor cannot import upward from the ee app. Only newcm is mapped — the
// sfo editor's MathService renders exclusively with MathJaxNewcmFont.
const NEWCM_DYNAMIC_FONT_LOADERS: Record<string, () => Promise<unknown>> = {
  'PUA': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/PUA.js'),
  'accents': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/accents.js'),
  'accents-b-i': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/accents-b-i.js'),
  'arabic': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/arabic.js'),
  'arrows': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/arrows.js'),
  'braille': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/braille.js'),
  'braille-d': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/braille-d.js'),
  'calligraphic': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/calligraphic.js'),
  'cherokee': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/cherokee.js'),
  'cyrillic': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/cyrillic.js'),
  'cyrillic-ss': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/cyrillic-ss.js'),
  'devanagari': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/devanagari.js'),
  'double-struck': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/double-struck.js'),
  'fraktur': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/fraktur.js'),
  'greek': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/greek.js'),
  'greek-ss': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/greek-ss.js'),
  'hebrew': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/hebrew.js'),
  'latin': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/latin.js'),
  'latin-b': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/latin-b.js'),
  'latin-bi': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/latin-bi.js'),
  'latin-i': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/latin-i.js'),
  'marrows': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/marrows.js'),
  'math': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/math.js'),
  'monospace': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/monospace.js'),
  'monospace-ex': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/monospace-ex.js'),
  'monospace-l': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/monospace-l.js'),
  'mshapes': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/mshapes.js'),
  'phonetics': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/phonetics.js'),
  'phonetics-ss': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/phonetics-ss.js'),
  'sans-serif': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/sans-serif.js'),
  'sans-serif-b': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/sans-serif-b.js'),
  'sans-serif-bi': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/sans-serif-bi.js'),
  'sans-serif-ex': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/sans-serif-ex.js'),
  'sans-serif-i': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/sans-serif-i.js'),
  'sans-serif-r': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/sans-serif-r.js'),
  'script': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/script.js'),
  'shapes': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/shapes.js'),
  'symbols': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/symbols.js'),
  'symbols-b-i': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/symbols-b-i.js'),
  'variants': () => import('@mathjax/mathjax-newcm-font/mjs/svg/dynamic/variants.js'),
};

interface MathJaxAsyncLoadHost {
  asyncLoad: ((name: string) => Promise<unknown>) | null;
  _newcmLoaderInstalled?: boolean;
}

// Wire `mathjax.asyncLoad` so MathJax's runtime requests for newcm font chunks
// are served from the static map above. Idempotent — the first call wins.
export function installNewcmAsyncLoader(mathjax: MathJaxAsyncLoadHost): void {
  if (mathjax._newcmLoaderInstalled) {
    return;
  }
  mathjax._newcmLoaderInstalled = true;
  mathjax.asyncLoad = async (name: string) => {
    const match = name.match(/svg\/dynamic\/([^/]+?)\.js$/);
    const key = match?.[1] ?? null;
    const loader = key ? NEWCM_DYNAMIC_FONT_LOADERS[key] : undefined;
    if (!loader) {
      throw new Error(`MathJax asyncLoad: no loader registered for "${name}"`);
    }
    const mod: any = await loader();
    return mod?.default ?? mod;
  };
}
